import { create } from "zustand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ConfirmDialogOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

interface ConfirmDialogState {
  isOpen: boolean;
  options: ConfirmDialogOptions | null;
  resolve: ((value: boolean) => void) | null;
}

const useConfirmDialogStore = create<ConfirmDialogState>(() => ({
  isOpen: false,
  options: null,
  resolve: null,
}));

export function useConfirmDialog() {
  const showConfirm = (options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      useConfirmDialogStore.setState({
        isOpen: true,
        options,
        resolve,
      });
    });
  };

  return { showConfirm };
}

export function ConfirmDialogProvider() {
  const { isOpen, options, resolve } = useConfirmDialogStore();

  const handleClose = (confirmed: boolean) => {
    useConfirmDialogStore.setState({ isOpen: false });
    if (resolve) {
      resolve(confirmed);
    }
    // Reset state after a delay to allow for animations
    setTimeout(() => {
      useConfirmDialogStore.setState({ options: null, resolve: null });
    }, 200);
  };

  if (!options) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{options.title}</DialogTitle>
          {options.description && (
            <DialogDescription>{options.description}</DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
          >
            {options.cancelText || "Cancel"}
          </Button>
          <Button
            variant={options.variant || "default"}
            onClick={() => handleClose(true)}
          >
            {options.confirmText || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

